(function () {
  'use strict';

  const BAR_SELECTOR = '.kaching-bundles__bar';
  const BARS_CONTAINER_SELECTOR = '.kaching-bundles__bars';
  const CONFIG_SELECTOR = '.bundle-property-config';
  const MOUNT_SELECTOR_CLASS = 'mount-type-selector';

  let optionGroupCounter = 0;

  function escapeHtml(value) {
    const el = document.createElement('span');
    el.textContent = value;
    return el.innerHTML;
  }

  function parseConfig(configEl) {
    try {
      const parsed = JSON.parse(configEl.textContent);
      const bundles = Array.isArray(parsed.bundles)
        ? parsed.bundles.map((bundle) => ({
            match: bundle.match || '',
            options: Array.isArray(bundle.options)
              ? bundle.options.filter((option) => option?.label && option?.value)
              : []
          }))
        : [];

      return {
        enabled: Boolean(parsed.enabled),
        propertyName: parsed.propertyName || '',
        label: parsed.label || '',
        bundles
      };
    } catch (error) {
      return { enabled: false, propertyName: '', label: '', bundles: [] };
    }
  }

  function getPropertyInputName(config) {
    return `properties[${config.propertyName}]`;
  }

  function getProductRoot(configEl) {
    return (
      configEl.closest('.shopify-section--main-product') ||
      configEl.closest('quick-buy-drawer') ||
      configEl.closest('product-rerender') ||
      configEl.parentElement
    );
  }

  function createContext(configEl) {
    const config = parseConfig(configEl);
    const root = getProductRoot(configEl);
    const form = root?.querySelector('form[action*="/cart/add"]') || null;

    return {
      configEl,
      config,
      root,
      form,
      propertyName: getPropertyInputName(config)
    };
  }

  function getBarConfig(bar, ctx) {
    const barText = (bar.textContent || '').toLowerCase();
    const bundles = [...ctx.config.bundles]
      .filter((bundle) => bundle.options.length)
      .sort((a, b) => (b.match?.length || 0) - (a.match?.length || 0));

    for (const bundle of bundles) {
      const matchText = (bundle.match || '').trim().toLowerCase();
      if (matchText && barText.includes(matchText)) {
        return {
          propertyName: ctx.config.propertyName,
          label: ctx.config.label,
          options: bundle.options
        };
      }
    }

    const bars = Array.from(ctx.root?.querySelectorAll(BAR_SELECTOR) || []);
    const barIndex = bars.indexOf(bar);
    const fallbackBundle = ctx.config.bundles[barIndex];

    if (fallbackBundle?.options?.length) {
      return {
        propertyName: ctx.config.propertyName,
        label: ctx.config.label,
        options: fallbackBundle.options
      };
    }

    return null;
  }

  function getBundleRadio(bar) {
    const radios = bar.querySelectorAll('input[type="radio"]');

    for (const radio of radios) {
      if (!radio.closest(`.${MOUNT_SELECTOR_CLASS}`)) {
        return radio;
      }
    }

    return null;
  }

  function getBundleLabel(bar) {
    const bundleRadio = getBundleRadio(bar);
    if (!bundleRadio) return null;

    const label = bundleRadio.closest('label');
    if (label && bar.contains(label)) return label;

    return bar.matches('label') ? bar : bar.querySelector('label');
  }

  function buildSelectorTemplate(groupName, barConfig) {
    const optionsHtml = barConfig.options
      .map((option, index) => {
        const isActive = index === 0;
        const iconStyle = option.icon
          ? ` style="--mount-option-icon: url('${String(option.icon).replace(/'/g, '%27')}'); --mount-option-icon-display: flex;"`
          : '';

        return `
          <label class="mount-option${isActive ? ' active' : ''}"${iconStyle}>
            <input type="radio" name="${groupName}" value="${escapeHtml(option.value)}"${isActive ? ' checked' : ''}>
            <span>${escapeHtml(option.label)}</span>
          </label>
        `;
      })
      .join('');

    const labelHtml = barConfig.label
      ? `<label class="mount-label">${escapeHtml(barConfig.label)}</label>`
      : '';

    return `
      <div class="${MOUNT_SELECTOR_CLASS}">
        ${labelHtml}
        <div class="mount-options">
          ${optionsHtml}
        </div>
      </div>
    `;
  }

  function ensurePropertyInput(ctx) {
    if (!ctx.propertyName) return null;

    if (ctx.form) {
      let input = ctx.form.querySelector(`input[name="${ctx.propertyName}"]`);
      if (input) return input;

      input = document.createElement('input');
      input.type = 'hidden';
      input.name = ctx.propertyName;
      ctx.form.appendChild(input);
      return input;
    }

    let input = ctx.root?.querySelector(`input[name="${ctx.propertyName}"]`);
    if (input) return input;

    return null;
  }

  function removePropertyInput(ctx, scope) {
    if (!ctx.propertyName) return;

    const containers = [ctx.form, scope, ctx.root].filter(Boolean);

    containers.forEach((container) => {
      container.querySelectorAll(`[name="${ctx.propertyName}"]`).forEach((input) => input.remove());
    });
  }

  function activeBarRequiresProperty(ctx) {
    const barsContainer = ctx.root?.querySelector(BARS_CONTAINER_SELECTOR);
    if (!barsContainer) return false;

    const activeBar = Array.from(barsContainer.querySelectorAll(BAR_SELECTOR)).find(
      (bar) => getBundleRadio(bar)?.checked
    );

    if (!activeBar) return false;

    const barConfig = getBarConfig(activeBar, ctx);
    const selectedOption = activeBar.querySelector(
      `.${MOUNT_SELECTOR_CLASS} input[type="radio"]:checked`
    );

    return Boolean(barConfig?.options.length && selectedOption);
  }

  function updateProperty(bar, ctx) {
    const bundleRadio = getBundleRadio(bar);
    if (!bundleRadio?.checked) return;

    const barConfig = getBarConfig(bar, ctx);
    const selectedOption = bar.querySelector(`.${MOUNT_SELECTOR_CLASS} input[type="radio"]:checked`);

    if (!barConfig?.options.length || !selectedOption) {
      removePropertyInput(ctx);
      return;
    }

    const input = ensurePropertyInput(ctx);
    if (input) input.value = selectedOption.value;
  }

  function syncPropertyFromActiveBundle(ctx) {
    const barsContainer = ctx.root?.querySelector(BARS_CONTAINER_SELECTOR);
    if (!barsContainer) {
      removePropertyInput(ctx);
      return;
    }

    const activeBar = Array.from(barsContainer.querySelectorAll(BAR_SELECTOR)).find(
      (bar) => getBundleRadio(bar)?.checked
    );

    if (!activeBar) {
      removePropertyInput(ctx);
      return;
    }

    updateProperty(activeBar, ctx);
  }

  function bindBarListeners(bar, ctx) {
    if (bar.dataset.bundlePropertyListen === 'true') return;

    const bundleRadio = getBundleRadio(bar);
    if (!bundleRadio) return;

    bar.dataset.bundlePropertyListen = 'true';

    bundleRadio.addEventListener('change', () => syncPropertyFromActiveBundle(ctx));
    bar.addEventListener('click', () => handleBundleBarClick(ctx));
  }

  function bindFormSubmit(ctx) {
    if (!ctx.form || ctx.form.dataset.bundlePropertySubmitBound === 'true') return;

    ctx.form.dataset.bundlePropertySubmitBound = 'true';

    const syncBeforeCart = () => {
      if (!activeBarRequiresProperty(ctx)) {
        removePropertyInput(ctx);
      } else {
        syncPropertyFromActiveBundle(ctx);
      }
    };

    ctx.form.addEventListener('submit', syncBeforeCart, true);
    ctx.form.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('button[type="submit"], input[type="submit"]');
        if (trigger && ctx.form.contains(trigger)) {
          syncBeforeCart();
        }
      },
      true
    );
  }

  function handleBundleBarClick(ctx) {
    requestAnimationFrame(() => syncPropertyFromActiveBundle(ctx));
  }

  function bindMountSelector(bar, ctx) {
    const barConfig = getBarConfig(bar, ctx);
    if (!barConfig?.options.length) return;

    const bundleRadio = getBundleRadio(bar);
    const bundleLabel = getBundleLabel(bar);

    if (!bundleRadio || !bundleLabel) return;

    if (bundleLabel.querySelector(`.${MOUNT_SELECTOR_CLASS}`)) {
      bar.dataset.bundlePropertyInit = 'true';
      return;
    }

    bar.dataset.bundlePropertyInit = 'true';

    const groupName = `bundle_property_${optionGroupCounter++}`;
    bundleLabel.insertAdjacentHTML('beforeend', buildSelectorTemplate(groupName, barConfig));

    const selector = bar.querySelector(`.${MOUNT_SELECTOR_CLASS}`);
    if (!selector) return;

    const optionInputs = selector.querySelectorAll('input[type="radio"]');

    optionInputs.forEach((input) => {
      input.addEventListener('change', () => {
        selector.querySelectorAll('.mount-option').forEach((opt) => opt.classList.remove('active'));
        input.closest('.mount-option')?.classList.add('active');
        updateProperty(bar, ctx);
      });
    });

    if (bundleRadio.checked) {
      syncPropertyFromActiveBundle(ctx);
    }
  }

  function hasAnyBundleOptions(config) {
    return config.bundles.some((bundle) => bundle.options.length);
  }

  function initConfig(configEl) {
    const ctx = createContext(configEl);

    if (!ctx.config.enabled || !ctx.config.propertyName || !hasAnyBundleOptions(ctx.config) || !ctx.root) {
      return;
    }

    ctx.root.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
      bindBarListeners(bar, ctx);
      bindMountSelector(bar, ctx);
    });

    bindFormSubmit(ctx);
    syncPropertyFromActiveBundle(ctx);
  }

  function init() {
    document.querySelectorAll(CONFIG_SELECTOR).forEach((configEl) => initConfig(configEl));
  }

  let initScheduled = false;

  function scheduleInit() {
    if (initScheduled) return;
    initScheduled = true;
    requestAnimationFrame(() => {
      initScheduled = false;
      init();
    });
  }

  function mutationsContainRelevantNodes(mutations) {
    return mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) return false;

        return (
          node.matches?.(BAR_SELECTOR) ||
          node.querySelector?.(BAR_SELECTOR) ||
          node.matches?.(CONFIG_SELECTOR) ||
          node.querySelector?.(CONFIG_SELECTOR)
        );
      })
    );
  }

  const observer = new MutationObserver((mutations) => {
    if (mutationsContainRelevantNodes(mutations)) {
      scheduleInit();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
